import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, BadgePercent } from "lucide-react";
import { formatPKR, fromPrice } from "@/lib/format";
import { cn } from "@/lib/utils";

export const ModelCard = ({ vehicle, featured = false, index = 0, offer = null }) => {
  const price = fromPrice(vehicle);
  return (
    <Link
      to={`/models/${vehicle.slug}`}
      data-testid={`model-card-${vehicle.slug}`}
      className={cn("group relative flex flex-col overflow-hidden border border-white/10 bg-surface transition-[border-color,transform] duration-500 hover:border-white/25", featured ? "min-h-[520px]" : "min-h-[420px]")}
    >
      <span className="absolute inset-x-0 top-0 h-px origin-left scale-x-0 bg-crimson transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-x-100" />
      <div className="flex items-start justify-between p-6 sm:p-8">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">{String(index + 1).padStart(2, "0")} — {vehicle.category}</p>
          <h3 className={cn("mt-3 font-display font-bold uppercase tracking-[-0.02em] text-white", featured ? "text-4xl sm:text-5xl" : "text-2xl sm:text-3xl")}>
            {vehicle.name.replace("JETOUR ", "")}
          </h3>
        </div>
        <div className="flex flex-col items-end gap-2">
          {offer && <span data-testid={`model-card-offer-${vehicle.slug}`} className="inline-flex items-center gap-1.5 rounded-full bg-gold px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-black"><BadgePercent className="h-3 w-3" /> Offer</span>}
          {vehicle.badge && <span className="rounded-full border border-gold/40 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-gold">{vehicle.badge}</span>}
        </div>
      </div>

      <div className={cn("relative flex flex-1 items-center justify-center px-6", featured ? "py-4" : "py-2")}>
        <div className="spotlight absolute inset-0 opacity-0 transition-opacity duration-700 group-hover:opacity-100" />
        <motion.img
          src={vehicle.hero_image}
          alt={vehicle.name}
          className={cn("relative z-10 w-full object-contain drop-shadow-[0_30px_40px_rgba(0,0,0,0.6)] transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-x-3 group-hover:scale-[1.04]", featured ? "max-h-[300px]" : "max-h-[190px]")}
          loading="lazy"
        />
        <div className="absolute bottom-6 left-1/2 h-6 w-3/4 -translate-x-1/2 rounded-[100%] bg-black/70 blur-xl" />
      </div>

      <div className="grid grid-cols-2 gap-x-6 gap-y-3 border-t border-white/10 p-6 sm:grid-cols-4 sm:p-8">
        {(vehicle.quick_specs || []).slice(0, 4).map((s) => (
          <div key={s.label}>
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">{s.label}</p>
            <p className="mt-1 font-display text-base font-semibold text-white">{s.value}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between border-t border-white/10 px-6 py-5 sm:px-8">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">{price ? "From" : "Booking open"}</p>
          <p className="font-display text-lg font-semibold text-white" data-testid={`model-card-price-${vehicle.slug}`}>{price ? formatPKR(price) : "Price to be announced"}</p>
        </div>
        <span className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white transition-[background-color,border-color,transform] duration-300 group-hover:bg-crimson group-hover:border-crimson group-hover:rotate-45">
          <ArrowUpRight className="h-5 w-5" />
        </span>
      </div>
    </Link>
  );
};
